import axios, { AxiosError } from 'axios';

export const OAUTH_API = process.env.REACT_APP_OAUTH_API || 'https://api.dev.4um.com/oauthservice';

const OAUTH_CREDENTIALS = { withCredentials: true } as const;

export const oauthClient = axios.create({
  baseURL: OAUTH_API,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

oauthClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    if (status === 401 || status === 403) {
      console.warn('[OAuth API] Auth error — oauth-service session cookie may be missing', {
        status,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        pathname: window.location.pathname,
        withCredentials: error.config?.withCredentials
      });
    }
    return Promise.reject(error);
  }
);

export interface AuthorizeParamsResponse {
  state: string;
  clientId: string;
  clientName: string;
  redirectUri: string;
  scopes: string[];
  codeChallenge: string;
  codeChallengeMethod: string;
  nonce?: string;
}

export interface ConsentMetadataResponse {
  state: string;
  clientId: string;
  clientName: string;
  requestedScopes: string[];
  approvedScopes: string[];
  userId: string;
  username: string;
}

export const getAuthorizeParams = async (state: string): Promise<AuthorizeParamsResponse> => {
  const response = await oauthClient.get<AuthorizeParamsResponse>('/auth/authorize/params', {
    params: { state },
    ...OAUTH_CREDENTIALS
  });
  return response.data;
};

export const establishSession = async (
  state: string,
  loginMethod?: 'email' | 'phone' | null
): Promise<void> => {
  await oauthClient.post(
    '/auth/session/establish',
    {
      state,
      ...(loginMethod ? { loginMethod } : {})
    },
    OAUTH_CREDENTIALS
  );
};

export const getConsentMetadata = async (state: string): Promise<ConsentMetadataResponse> => {
  const response = await oauthClient.get<ConsentMetadataResponse>('/auth/consent/metadata', {
    params: { state },
    ...OAUTH_CREDENTIALS
  });
  return response.data;
};

export const approveConsent = async (payload: {
  state: string;
  clientId: string;
  approvedScopes: string[];
}): Promise<void> => {
  await oauthClient.post('/auth/consent/approve', payload, OAUTH_CREDENTIALS);
};

export const buildResumeAuthorizeUrl = (
  params: AuthorizeParamsResponse,
  approvedScopes?: string[],
  loginMethod: 'email' | 'phone' = 'email'
): string => {
  const scopes =
    approvedScopes && approvedScopes.length > 0 ? approvedScopes : params.scopes;
  // Mandatory scopes for the active login method must remain if originally requested.
  // Opposite identity scope is never re-requested (avoids consent redirect loops).
  const hiddenScope = loginMethod === 'email' ? 'phone' : 'email';
  const scopeSet = new Set(scopes.filter((scope) => scope !== hiddenScope));
  if (params.scopes.includes('openid')) {
    scopeSet.add('openid');
  }
  if (params.scopes.includes('profile')) {
    scopeSet.add('profile');
  }
  if (loginMethod === 'email' && params.scopes.includes('email')) {
    scopeSet.add('email');
  }
  if (loginMethod === 'phone' && params.scopes.includes('phone')) {
    scopeSet.add('phone');
  }
  scopeSet.delete(hiddenScope);
  const query = new URLSearchParams({
    response_type: 'code',
    client_id: params.clientId,
    state: params.state,
    redirect_uri: params.redirectUri,
    scope: Array.from(scopeSet).join(' '),
    code_challenge: params.codeChallenge,
    code_challenge_method: params.codeChallengeMethod
  });
  if (params.nonce) {
    query.set('nonce', params.nonce);
  }
  return `${OAUTH_API}/oauth2/authorize?${query.toString()}`;
};
